<template>
  <div>
    <PageAllow controller="ShopSales" action="view" />
    <Breadcrumbs :items="items" />

    <v-sheet class="overflow-y-scroll fill-content-height">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherOrderDate
              v-model="options"
              show-radio
              show-radio-actual
              show-radio-expect
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn small color="print" dark @click.stop="exportExcel()">
              <v-icon x-small class="pe-1">fa-file-excel</v-icon>
              {{ $t("Export.excel", [$t("Nav.shopSalesDetails", [$consts.shopName()])]) }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherAlliances v-model="alliancesSelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherShop
                  v-model="shopSelected"
                  districtID="0"
                  :allianceBusinessIds="options.alliance_business_ids"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherOrderOriginFlgs v-model="options.origin_flgs" />
              </v-col>
              <v-col cols="12" sm="12" md="6">
                <SearcherOrderStatus v-model="options.status" />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <div
                  class="caption grey--text input-chip-group-label"
                  :class="
                    $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                  "
                >
                  {{ $t("ShopSales.DetailTableHeaders.diff") }}
                </div>
                <v-chip-group v-model="options.bill_fee_diff" column>
                  <v-chip
                    small
                    label
                    filter
                    class="me-1"
                    color="accent"
                    text-color="gray"
                    :value="1"
                    >{{$t("ShopSales.DetailTableHeaders.billFeeDiff")}}
                  </v-chip>
                </v-chip-group>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherCancelFlgs v-model="options.cancel_flgs" />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="shopSales"
          :options.sync="options"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-1 px-4 pt-1"
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:body.prepend>
            <tr class="d-none d-sm-table-row">
              <td>
                <span>{{ $t("TableSum") }}</span>
              </td>
              <td :align="$vuetify.rtl ? 'left' : 'right'">
                <span
                  >{{ $t("Global.money") }}
                  {{ shopSalesTotalOrderPrice | formatMoney }}</span
                >
              </td>
              <td :align="$vuetify.rtl ? 'left' : 'right'">
                <span
                  >{{ $t("Global.money") }}
                  {{ shopSalesTotalFee | formatMoney }}</span
                >
              </td>
              <td :align="$vuetify.rtl ? 'left' : 'right'">
                <span
                  >{{ $t("Global.money") }}
                  {{ shopSalesTotalLogisticsFee | formatMoney }}</span
                >
              </td>
              <td colspan="5"></td>
            </tr>
            <tr class="d-table-row d-sm-none">
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{ $t("TableSum") }}
                </div>
              </td>
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{ $t("ShopSales.DetailTableHeaders.orderPrice") }}
                </div>
                <div class="v-data-table__mobile-row__cell">
                  {{ $t("Global.money") }}
                  {{ shopSalesTotalOrderPrice | formatMoney }}
                </div>
              </td>
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{ $t("ShopSales.DetailTableHeaders.fee") }}
                </div>
                <div class="v-data-table__mobile-row__cell">
                  {{ $t("Global.money") }}
                  {{ shopSalesTotalFee | formatMoney }}
                </div>
              </td>
              <td class="v-data-table__mobile-row">
                <div class="v-data-table__mobile-row__header">
                  {{ $t("ShopSales.TableHeaders.logisticsFee") }}
                </div>
                <div class="v-data-table__mobile-row__cell">
                  {{ $t("Global.money") }}
                  {{ shopSalesTotalLogisticsFee | formatMoney }}
                </div>
              </td>
            </tr>
          </template>

          <template v-slot:item.order_price="{ item }"
            >{{ $t("Global.money") }}
            {{ item.order_price | formatMoney }}</template
          >

          <template v-slot:item.bill.logistics_fee="{ item }"
            >{{ $t("Global.money") }}
            {{ item.bill.logistics_fee | formatMoney }}</template
          >
          <template v-slot:item.bill.fee="{ item }"
            >{{ $t("Global.money") }}
            {{ item.bill.fee | formatMoney }}</template
          >

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | orderStatusColor"
              :text-color="item.status | orderStatusTextColor"
              >{{ item.status | orderStatusText }}</v-chip
            >
          </template>
          <template v-slot:item.bill.pay_flg="{ item }">
            <v-chip
              small
              label
              :color="item.bill.pay_flg | billPayFlgsColor"
              :text-color="item.bill.pay_flg | billPayFlgsTextColor"
              >{{ item.bill.pay_flg | billPayFlgsText }}</v-chip
            >
          </template>
          <template v-slot:item.bill.status="{ item }">
            <v-chip
              small
              label
              :color="item.bill.status | billStatusColor"
              :text-color="item.bill.status | billStatusTextColor"
              >{{ item.bill.status | billStatusText }}</v-chip
            >
          </template>
          <template v-slot:item.origin_flg="{ item }">
            <v-chip small chips>{{
              item.origin_flg | orderOriginFlgsText
            }}</v-chip>
          </template>
          <template v-slot:item.created="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="caption font-weight-thin grey--text"
                  :class="
                    $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                  "
                >
                  {{ formatOrderDate(item, false) }}
                </div>
              </template>
              <span>{{ formatOrderDate(item, true) }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherOrderDate from "@/components/plugins/SearcherOrderDate";
import SearcherAlliances from "@/components/plugins/SearcherAlliances";
import SearcherShop from "@/components/plugins/SearcherShop";
import SearcherOrderStatus from "@/components/plugins/SearcherOrderStatus";
import SearcherOrderOriginFlgs from "@/components/plugins/SearcherOrderOriginFlgs";
import Shops from "@/api/shops";
import ShopSales from "@/api/shop_sales";
import SearcherCancelFlgs from "@/components/plugins/SearcherCancelFlgs";
import consts from "@/consts";

const defaultOptions = {
  orderDatetimeRadios: "radio-created",
  created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  actual_time_from: "",
  actual_time_to: "",
  expect_time_from: "",
  expect_time_to: "",
  shop_ids: [],
  status: [1, 2, 3, 4, 5, 6],
  origin_flgs: [],
  sortBy: [],
  sortDesc: [],
  cancel_flgs: [0],
  bill_fee_diff: 0,
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherOrderDate,
    SearcherAlliances,
    SearcherShop,
    SearcherOrderStatus,
    SearcherOrderOriginFlgs,
    SearcherCancelFlgs,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.shopSales", [consts.shopName()]),
        disabled: false,
        href: "/shop-sales",
      },
      {
        text: i18n.t("Nav.shopSalesDetails", [consts.shopName()]),
        disabled: true,
        href: "",
      },
    ],
    headers: () => [
      {
        text: i18n.t("ShopSales.DetailTableHeaders.orderCode"),
        align: "start",
        sortable: false,
        value: "order_code",
      },
      {
        text: i18n.t("ShopSales.DetailTableHeaders.orderPrice"),
        align: "end",
        sortable: false,
        value: "order_price",
      },
      {
        text: i18n.t("ShopSales.DetailTableHeaders.fee"),
        align: "end",
        sortable: false,
        value: "bill.fee",
      },

      {
        text: i18n.t("ShopSales.DetailTableHeaders.logisticsFee"),
        align: "end",
        sortable: false,
        value: "bill.logistics_fee",
      },

      {
        text: i18n.t("ShopSales.DetailTableHeaders.status"),
        align: "center",
        sortable: false,
        value: "status",
      },
      {
        text: i18n.t("ShopSales.DetailTableHeaders.billPayFlg"),
        align: "center",
        sortable: false,
        value: "bill.pay_flg",
      },
      {
        text: i18n.t("ShopSales.DetailTableHeaders.billStatus"),
        align: "center",
        sortable: false,
        value: "bill.status",
      },
      {
        text: i18n.t("ShopSales.DetailTableHeaders.originFlg"),
        align: "center",
        sortable: false,
        value: "origin_flg",
      },
      {
        text: i18n.t("ShopSales.DetailTableHeaders.orderDate"),
        align: "start",
        sortable: false,
        value: "created",
      },
    ],
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  mounted() {
    this.getShopFromAPI();
    this.$commonUtils.loadOptions("shop-sales", ["alliancesSelected"]).then(
      function (data) {
        if (data.options) {
          this.options = data.options;
          this.options.page = 1;
        } else this.resetSearch();

        data.selecteds.forEach((selected) => {
          switch (selected.key) {
            case "alliancesSelected":
              this.alliancesSelected = selected.value;
              break;
          }
        });
      }.bind(this)
    );
  },

  data: () => ({
    shop: null,
    alliancesSelected: [],
    shopSelected: null,
    options: JSON.parse(JSON.stringify(defaultOptions)),
    paging: {},
    shopSales: [],
    shopSalesTotalCount: 0,
    shopSalesTotalOrderPrice: 0,
    shopSalesTotalFee: 0,
    shopSalesTotalLogisticsFee: 0,
  }),

  methods: {
    resetSearch() {
      this.alliancesSelected = [];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },
    onSearch() {
      this.getShopSalesFromAPI();
    },
    getShopFromAPI() {
      Shops.fetchOne(this.$route.params.shop_id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.shop = response.data.shop;
            this.shopSelected = this.shop;
          }
        }.bind(this)
      );
    },
    formatOrderDate(orderItem, withTime) {
      if (
        this.$datetimeUtils.formatDate(orderItem.created) ==
        this.$datetimeUtils.formatDate(orderItem.actual_date)
      )
        return withTime
          ? this.$datetimeUtils.formatTime(orderItem.created)
          : this.$datetimeUtils.formatDate(orderItem.created);
      else
        return withTime
          ? this.$datetimeUtils.formatTime(orderItem.created) +
              " / " +
              this.$datetimeUtils.formatTime(orderItem.actual_date)
          : this.$datetimeUtils.formatDate(orderItem.created) +
              " / " +
              this.$datetimeUtils.formatDate(orderItem.actual_date);
    },
    getShopSalesFromAPI() {
      ShopSales.fetchOne(this.$route.params.shop_id, this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.shopSales = response.data.orders;
            this.shopSalesTotalOrderPrice =
              response.data.orders_total_order_price;
            this.shopSalesTotalFee = response.data.orders_total_bill_price;

            this.shopSalesTotalLogisticsFee = response.data.total_logistics_fee;
            this.paging = response.data.paging;
            if (this.shopSales.length == 0) {
              this.options.page = 1;
            }
          } else if (response.data.error.code.isNoResultSet) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    exportExcel() {
      ShopSales.exportOne(this.$route.params.shop_id, this.options);
    },
  },

  watch: {
    alliancesSelected: {
      handler() {
        this.options.alliance_business_ids = this.alliancesSelected.map(
          (alliance) => {
            return alliance.id;
          }
        );
        sessionStorage.setItem(
          "shop-sales-alliancesSelected",
          JSON.stringify(this.alliancesSelected)
        );
      },
    },
    shopSelected: {
      handler() {
        if (
          this.shopSelected &&
          this.shopSelected.id != this.$route.params.shop_id
        ) {
          this.$router.replace("/shop-sales/" + this.shopSelected.id);
          this.getShopSalesFromAPI();
        }
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getShopSalesFromAPI();
        sessionStorage.setItem(
          "shop-sales-options",
          JSON.stringify(this.options)
        );
      },
    },
  },

  beforeDestroy() {
    this.$store.dispatch("threadLoadingStopAll");
  },
};
</script>